import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  LoadingOverlay,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BasicQuestionEditorModal } from "./basic-question-editor-modal";
import {
  toReviewBasicQuestions,
  toReviewCompetencies,
  toTemplatePayload,
} from "./company-question-template-mappers";
import { DefaultQuestionsSection } from "./default-questions-section";
import { DefaultStatementsSection } from "./default-statements-section";
import {
  DUPLICATE_NAME_MESSAGE,
  EDIT_COMPANY_MODAL_OVERLAY_CLASS,
  EDIT_COMPANY_MODAL_TEST_IDS,
  INPUT_CLASS_NAMES,
  NOTIFICATION_MESSAGES,
} from "./edit-company-modal.constants";
import type {
  BasicQuestionEditorState,
  EditCompanyModalProps,
  WrittenQuestionEditorState,
} from "./edit-company-modal.types";
import { CodeToken } from "./template-textarea";
import { WrittenQuestionEditorModal } from "./written-question-editor-modal";
import { isCompanyQuestionTemplatesEnabled } from "@/constants/company-question-templates-feature";
import {
  useCompanyManageDetailQuery,
  useCompanyQuestionTemplateQuery,
  useUpdateCompanyDefaultsManageMutation,
  useUpdateCompanyManageMutation,
} from "@/hooks/queries/company.queries";
import {
  QuestionType,
  type ReviewPageBasicQuestion,
  type ReviewPageCompetency,
  type ReviewPageDraftQuestion,
  type QuestionStateSetter,
} from "@/types/screener-builder";
import type { Nullable } from "@/types/utils";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";
import { getDraftBasicQuestion } from "@/utils/questions";
import { isEmptyAfterTrim, trimStringValues, trimToNull } from "@/utils/string";

export {
  EDIT_COMPANY_MODAL_OVERLAY_CLASS,
  EDIT_COMPANY_MODAL_TEST_IDS,
} from "./edit-company-modal.constants";

export const EditCompanyModal = ({
  companies,
  companyId,
  onClose,
  opened,
}: EditCompanyModalProps) => {
  const [name, setName] = useState("");
  const [defaultOpeningStatement, setDefaultOpeningStatement] = useState("");
  const [defaultClosingStatement, setDefaultClosingStatement] = useState("");
  // TODO(AS-1925): Company-default AI generation is intentionally disabled for
  // this PR. Re-enable modal-session job title/description state and the
  // enable/disable UI when the AI follow-up resumes.
  const [basicQuestions, setBasicQuestions] = useState<
    ReviewPageBasicQuestion[]
  >([]);
  const [writtenQuestions, setWrittenQuestions] = useState<
    ReviewPageCompetency[]
  >([]);
  const [basicQuestionEditor, setBasicQuestionEditor] =
    useState<Nullable<BasicQuestionEditorState>>(null);
  const [writtenQuestionEditor, setWrittenQuestionEditor] =
    useState<Nullable<WrittenQuestionEditorState>>(null);
  const initializedCompanyIdRef = useRef<Nullable<string>>(null);
  const companyQuestionTemplatesEnabled = isCompanyQuestionTemplatesEnabled();

  const { data: companyDetail, isLoading: isCompanyDetailLoading } =
    useCompanyManageDetailQuery(companyId, { enabled: opened });
  const {
    data: questionTemplate,
    isError: isQuestionTemplateError,
    isLoading: isQuestionTemplateLoading,
  } = useCompanyQuestionTemplateQuery(companyId, {
    enabled: opened && companyQuestionTemplatesEnabled,
  });
  const { mutateAsync: updateCompany, isPending: isUpdatePending } =
    useUpdateCompanyManageMutation();
  const {
    mutateAsync: updateCompanyDefaults,
    isPending: isDefaultsUpdatePending,
  } = useUpdateCompanyDefaultsManageMutation();

  const shouldLoadQuestionTemplate =
    opened && companyQuestionTemplatesEnabled && companyId != null;
  const hasQuestionTemplateLoadError =
    shouldLoadQuestionTemplate && isQuestionTemplateError;
  const isQuestionTemplatePending =
    shouldLoadQuestionTemplate &&
    !isQuestionTemplateError &&
    (isQuestionTemplateLoading || questionTemplate === undefined);
  const isLoading = isCompanyDetailLoading || isQuestionTemplatePending;
  const isMutationPending =
    isUpdatePending ||
    (companyQuestionTemplatesEnabled && isDefaultsUpdatePending);

  useEffect(() => {
    if (!opened) {
      initializedCompanyIdRef.current = null;
      return;
    }

    if (
      !companyId ||
      !companyDetail ||
      companyDetail.id !== companyId ||
      isQuestionTemplatePending
    ) {
      return;
    }

    if (hasQuestionTemplateLoadError) {
      if (initializedCompanyIdRef.current !== companyId) {
        setName(companyDetail.name ?? "");
        setDefaultOpeningStatement(companyDetail.defaultOpeningStatement ?? "");
        setDefaultClosingStatement(companyDetail.defaultClosingStatement ?? "");
        setBasicQuestions([]);
        setWrittenQuestions([]);
        initializedCompanyIdRef.current = companyId;
      }

      return;
    }

    if (initializedCompanyIdRef.current === companyId) {
      return;
    }

    const hasTemplate =
      companyQuestionTemplatesEnabled && questionTemplate != null;
    const companyOpeningStatement = companyDetail.defaultOpeningStatement ?? "";
    const companyClosingStatement = companyDetail.defaultClosingStatement ?? "";

    setName(companyDetail.name ?? "");
    setDefaultOpeningStatement(
      hasTemplate
        ? (questionTemplate.openingStatement ?? companyOpeningStatement)
        : companyOpeningStatement,
    );
    setDefaultClosingStatement(
      hasTemplate
        ? (questionTemplate.closingStatement ?? companyClosingStatement)
        : companyClosingStatement,
    );
    setBasicQuestions(
      companyQuestionTemplatesEnabled
        ? toReviewBasicQuestions(questionTemplate)
        : [],
    );
    setWrittenQuestions(
      companyQuestionTemplatesEnabled
        ? toReviewCompetencies(questionTemplate)
        : [],
    );
    initializedCompanyIdRef.current = companyId;
  }, [
    companyDetail,
    companyId,
    companyQuestionTemplatesEnabled,
    hasQuestionTemplateLoadError,
    isQuestionTemplatePending,
    opened,
    questionTemplate,
  ]);

  const resetState = useCallback(() => {
    setName("");
    setDefaultOpeningStatement("");
    setDefaultClosingStatement("");
    setBasicQuestions([]);
    setWrittenQuestions([]);
    setBasicQuestionEditor(null);
    setWrittenQuestionEditor(null);
    initializedCompanyIdRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const trimmedName = name.trim();
  const isDuplicateName = useMemo(() => {
    const normalizedName = trimmedName.toLowerCase();

    if (!normalizedName) {
      return false;
    }

    return companies.some(
      (company) =>
        company.id !== companyId &&
        company.name.trim().toLowerCase() === normalizedName,
    );
  }, [companies, companyId, trimmedName]);

  const nameError = isDuplicateName
    ? DUPLICATE_NAME_MESSAGE
    : isEmptyAfterTrim(name) && !isLoading
      ? "Company name is required"
      : null;
  const isSaveDisabled =
    !companyId ||
    isEmptyAfterTrim(name) ||
    isDuplicateName ||
    isLoading ||
    hasQuestionTemplateLoadError ||
    isMutationPending;
  const footerCompanyName = companyDetail?.name ?? trimmedName;
  const footerCompanyReference = isEmptyAfterTrim(footerCompanyName)
    ? "this company"
    : footerCompanyName;

  const closeBasicQuestionEditor = useCallback(() => {
    setBasicQuestionEditor(null);
  }, []);

  const closeWrittenQuestionEditor = useCallback(() => {
    setWrittenQuestionEditor(null);
  }, []);

  const openBasicQuestionEditor = useCallback(() => {
    if (!companyQuestionTemplatesEnabled) {
      return;
    }

    setBasicQuestionEditor((current) => current ?? {});
  }, [companyQuestionTemplatesEnabled]);

  const selectBasicQuestion = useCallback(
    (question: ReviewPageBasicQuestion) => {
      setBasicQuestionEditor({
        draftQuestion: question,
        selectedQuestion: question,
      });
    },
    [],
  );

  const setBasicQuestionDraft = useCallback<QuestionStateSetter>(
    (questionAction) => {
      setBasicQuestionEditor((current) => {
        if (!current) {
          return current;
        }

        if (typeof questionAction !== "function") {
          return { ...current, draftQuestion: questionAction };
        }

        const previousQuestion =
          current.draftQuestion ?? current.selectedQuestion;

        if (!previousQuestion) {
          return current;
        }

        return {
          ...current,
          draftQuestion: questionAction(previousQuestion),
        };
      });
    },
    [],
  );

  const handleBasicQuestionTypeSelect = useCallback(
    (questionType: QuestionType) => {
      setBasicQuestionEditor((current) =>
        current
          ? {
              ...current,
              draftQuestion: getDraftBasicQuestion(questionType),
            }
          : current,
      );
    },
    [],
  );

  const handleBasicQuestionSubmit = useCallback(
    (question: ReviewPageDraftQuestion) => {
      const trimmedQuestion = trimStringValues(question);

      if (isEmptyAfterTrim(trimmedQuestion.questionText)) {
        return;
      }

      setBasicQuestions((questions) => {
        if (basicQuestionEditor?.selectedQuestion) {
          return questions.map((existingQuestion) =>
            existingQuestion.id === basicQuestionEditor.selectedQuestion?.id
              ? {
                  ...existingQuestion,
                  ...trimmedQuestion,
                  id: existingQuestion.id,
                  isCustom: existingQuestion.isCustom,
                  rootId: existingQuestion.rootId,
                }
              : existingQuestion,
          );
        }

        return questions.concat({
          ...trimmedQuestion,
          id: nanoid(),
          isCustom: true,
          rootId: undefined,
        });
      });
      setBasicQuestionEditor(null);
    },
    [basicQuestionEditor],
  );

  const openWrittenQuestionEditor = useCallback(
    (competency: Nullable<ReviewPageCompetency>) => {
      if (!companyQuestionTemplatesEnabled) {
        return;
      }

      setWrittenQuestionEditor({
        competency: competency ?? undefined,
      });
    },
    [companyQuestionTemplatesEnabled],
  );

  const handleNameBlur = useCallback(() => {
    setName(trimmedName);
  }, [trimmedName]);

  const handleSave = useCallback(async () => {
    if (!companyId || isSaveDisabled) {
      return;
    }

    const companyUpdate = {
      defaultClosingStatement: trimToNull(defaultClosingStatement),
      defaultOpeningStatement: trimToNull(defaultOpeningStatement),
      name: trimmedName,
    };
    const questionTemplate = {
      ...toTemplatePayload({
        basicQuestions,
        closingStatement: defaultClosingStatement,
        openingStatement: defaultOpeningStatement,
        writtenQuestions,
      }),
    };

    try {
      if (companyQuestionTemplatesEnabled) {
        await updateCompanyDefaults({
          company: companyUpdate,
          companyId,
          questionTemplate,
        });
      } else {
        await updateCompany({
          companyId,
          ...companyUpdate,
        });
      }

      showUpwageNotification(
        NOTIFICATION_MESSAGES.updateSuccess,
        NotificationTypes.Success,
      );
      handleClose();
    } catch {
      showUpwageNotification(
        NOTIFICATION_MESSAGES.updateError,
        NotificationTypes.Error,
      );
    }
  }, [
    basicQuestions,
    companyId,
    companyQuestionTemplatesEnabled,
    defaultClosingStatement,
    defaultOpeningStatement,
    handleClose,
    isSaveDisabled,
    trimmedName,
    updateCompany,
    updateCompanyDefaults,
    writtenQuestions,
  ]);

  const disabled =
    isLoading || isMutationPending || hasQuestionTemplateLoadError;

  return (
    <>
      <Modal
        aria-label="Edit company"
        centered
        closeOnClickOutside={!isMutationPending}
        closeOnEscape={!isMutationPending}
        onClose={handleClose}
        opened={opened}
        overlayProps={{
          className: EDIT_COMPANY_MODAL_OVERLAY_CLASS,
        }}
        padding={0}
        radius={6}
        size={900}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          },
          content: {
            border: "1px solid var(--mantine-color-gray-2)",
            boxShadow: "0px 0px 24px 0px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 32px)",
            maxHeight: "calc(100vh - 32px)",
            overflow: "hidden",
          },
        }}
        withCloseButton={false}
      >
        <Flex className="h-full min-h-0" direction="column">
          <LoadingOverlay visible={isLoading} />

          <Flex
            align="center"
            className="shrink-0 border-b border-uw-gray-1 px-6 py-4"
            justify="space-between"
          >
            <div>
              <Text className="text-lg font-semibold leading-6 text-uw-gray-9">
                Edit company
              </Text>
              {companyDetail?.name && (
                <Text c="gray.6" className="mt-1 text-sm font-medium">
                  {companyDetail.name}
                </Text>
              )}
            </div>
            <ActionIcon
              aria-label="Close edit company"
              className="rounded-full border-0 bg-uw-gray-0 text-uw-gray-8 shadow-none hover:bg-uw-gray-1 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-0"
              data-testid={EDIT_COMPANY_MODAL_TEST_IDS.closeButton}
              disabled={isMutationPending}
              onClick={handleClose}
              size={28}
              variant="transparent"
            >
              <FontAwesomeIcon className="text-xs" icon={faXmark} />
            </ActionIcon>
          </Flex>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <Flex direction="column" gap={20}>
              <TextInput
                classNames={INPUT_CLASS_NAMES}
                data-testid={EDIT_COMPANY_MODAL_TEST_IDS.nameInput}
                disabled={disabled}
                error={nameError}
                label="Company name"
                onBlur={handleNameBlur}
                onChange={(event) => setName(event.currentTarget.value)}
                size="sm"
                value={name}
              />

              <Divider
                className="border-uw-gray-2"
                styles={{ root: { borderTopStyle: "dashed" } }}
              />

              <div>
                <Text className="text-base font-bold leading-5 text-uw-gray-9">
                  Screener defaults
                </Text>
                <Text className="mt-2 max-w-[760px] text-sm font-medium leading-5 text-uw-gray-7">
                  Seed every <strong>new</strong>
                  {
                    " screener with a consistent starting point — opening & closing messages"
                  }
                  {companyQuestionTemplatesEnabled
                    ? " plus a default question set"
                    : ""}
                  {". Use "}
                  <CodeToken>{"{{company_name}}"}</CodeToken>
                  {" and "}
                  <CodeToken>{"{{job_title}}"}</CodeToken>
                  {
                    " as placeholders in Default Intro. Default Conclusion is sent exactly as written."
                  }
                </Text>
              </div>

              <Flex
                align="flex-start"
                className="rounded-[4px] border-0 border-l-[3px] border-solid border-l-uw-sky-blue-7 bg-uw-sky-blue-0 px-4 py-3 text-uw-sky-blue-9"
                gap={10}
              >
                <FontAwesomeIcon
                  className="mt-0.5 shrink-0 text-sm"
                  icon={faCircleInfo}
                />
                <Text c="inherit" className="text-sm font-medium leading-5">
                  Saving these defaults will <strong>not</strong> change
                  existing screeners — only screeners created after this point.
                  New screeners are seeded with the messages
                  {companyQuestionTemplatesEnabled && (
                    <>
                      {" "}
                      <strong>and questions</strong>
                    </>
                  )}{" "}
                  below.
                </Text>
              </Flex>

              {hasQuestionTemplateLoadError && (
                <Flex
                  align="flex-start"
                  className="rounded-[4px] border-0 border-l-[3px] border-solid border-l-red-5 bg-red-0 px-4 py-3 text-red-9"
                  data-testid={EDIT_COMPANY_MODAL_TEST_IDS.defaultsLoadError}
                  gap={10}
                >
                  <FontAwesomeIcon
                    className="mt-0.5 shrink-0 text-sm"
                    icon={faCircleInfo}
                  />
                  <Text c="inherit" className="text-sm font-medium leading-5">
                    Screener defaults could not be loaded. Close and reopen this
                    company before saving changes.
                  </Text>
                </Flex>
              )}

              <DefaultStatementsSection
                defaultClosingStatement={defaultClosingStatement}
                defaultOpeningStatement={defaultOpeningStatement}
                disabled={disabled}
                setDefaultClosingStatement={setDefaultClosingStatement}
                setDefaultOpeningStatement={setDefaultOpeningStatement}
              />

              {companyQuestionTemplatesEnabled &&
                !hasQuestionTemplateLoadError && (
                  <DefaultQuestionsSection
                    basicQuestions={basicQuestions}
                    disabled={disabled}
                    openBasicQuestionEditor={openBasicQuestionEditor}
                    openWrittenQuestionEditor={openWrittenQuestionEditor}
                    selectBasicQuestion={selectBasicQuestion}
                    setBasicQuestions={setBasicQuestions}
                    setWrittenQuestions={setWrittenQuestions}
                    writtenQuestions={writtenQuestions}
                  />
                )}
            </Flex>
          </div>

          <Flex
            align="center"
            className="shrink-0 border-t border-uw-gray-1 bg-white px-6 py-4"
            gap={16}
            justify="space-between"
          >
            <Text
              c="gray.6"
              className="max-w-[420px] text-xs font-medium leading-5"
            >
              Messages
              {companyQuestionTemplatesEnabled ? " & questions" : ""} apply to{" "}
              <strong>all new screeners</strong> at {footerCompanyReference}{" "}
              from save onward.
            </Text>
            <Flex gap={12}>
              <Button
                className="h-10 px-4 text-sm font-semibold"
                color="gray.0"
                c="gray.9"
                data-testid={EDIT_COMPANY_MODAL_TEST_IDS.cancelButton}
                disabled={isMutationPending}
                onClick={handleClose}
                radius={4}
              >
                Cancel
              </Button>
              <Button
                className="h-10 px-4 text-sm font-semibold"
                color="purple.5"
                data-testid={EDIT_COMPANY_MODAL_TEST_IDS.saveButton}
                disabled={isSaveDisabled}
                loading={isMutationPending}
                onClick={handleSave}
                radius={4}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Modal>

      <BasicQuestionEditorModal
        editor={basicQuestionEditor}
        isMutationPending={isMutationPending}
        onClose={closeBasicQuestionEditor}
        onQuestionTypeSelect={handleBasicQuestionTypeSelect}
        onSubmit={handleBasicQuestionSubmit}
        setQuestion={setBasicQuestionDraft}
      />

      {companyQuestionTemplatesEnabled && (
        <WrittenQuestionEditorModal
          editor={writtenQuestionEditor}
          onClose={closeWrittenQuestionEditor}
          onExitTransitionEnd={() => setWrittenQuestionEditor(null)}
          setWrittenQuestions={setWrittenQuestions}
          writtenQuestions={writtenQuestions}
        />
      )}
    </>
  );
};
